import { FlexCol } from '@/components/common/Flex';
import { GoalType } from '@common/graphql/generated';
import { useState } from 'react';
import GoalDetail from './GoalDetail';
import { GoalDetailProvider } from './GoalDetailContext';
import GoalOnboardingAccountType from './onboarding/GoalOnboardingAccountType';

type GoalDetailDrawerProps = {
    onClose: () => void;
    id: string | GoalType; // either goal ID or goal type
};

// This implementation:
// 1. Creates a const array with all valid goal types, using `as const` to make it a readonly tuple
// 2. The type system will infer the exact string literal union type from this array
// 3. If the `GoalType` union ever changes, this code will cause a type error since the array values won't match the union type
// 4. Uses `includes()` to check if the input string matches any valid goal type
// 5. The type predicate `idOrType is GoalType` narrows the type when the function returns true
// The only type assertion needed is the as GoalType in the includes check, which is safe because we're comparing against the const array that matches the union type.
// This approach is better than hardcoding the check with a large OR condition because it's maintainable - if GoalType changes, TypeScript will force us to update the GOAL_TYPES array.
// ~Cursor
const GOAL_TYPES = [
    'BABY',
    'CAR',
    'CASH_MANAGEMENT',
    'COLLEGE',
    'CUSTOM',
    'DAYCARE',
    'EMERGENCY_FUND',
    'GENERAL_INVESTING',
    'HOME_DOWN_PAYMENT',
    'PAY_OFF_DEBT',
    'RETIREMENT',
    'VACATION',
    'WEDDING',
] as const;
const isGoalType = (idOrType: string | GoalType): idOrType is GoalType => {
    return GOAL_TYPES.includes(idOrType as GoalType);
};

const GoalDetailDrawer = ({ id: idOrType, onClose }: GoalDetailDrawerProps) => {
    const [goalId, setGoalId] = useState<string | undefined>(
        isGoalType(idOrType) ? undefined : idOrType
    );

    if (goalId) {
        return (
            <GoalDetailProvider id={goalId} onClose={onClose}>
                <GoalDetail />
            </GoalDetailProvider>
        );
    }

    if (isGoalType(idOrType)) {
        return (
            <FlexCol overflow="hidden" h="full">
                <GoalOnboardingAccountType
                    goalType={idOrType}
                    onComplete={(id: string) => {
                        setGoalId(id);
                    }}
                />
            </FlexCol>
        );
    }

    console.warn('Invalid goal detail drawer state', { idOrType });
    return <></>;
};

export default GoalDetailDrawer;
